import type { HubConnection } from "@microsoft/signalr";
import { logError } from "@entrilia/shared/logging";
import { getRestartDelayMs } from "@entrilia/shared/services/signalRConnector";
import type { ChangedResourceType, ResourceChangedNotification } from "../api/types/resourceChangesTypes";

const RESOURCE_CHANGED_EVENT = "resourceChanged";

export interface ResourceChangesListener {
  onChange: (notification: ResourceChangedNotification) => void;
  // Called after every (re)connect: events sent while the connection was not subscribed are lost.
  onSubscribed: () => void;
}

interface Options {
  connect: () => Promise<HubConnection | undefined>;
  getSubscriptionToken: () => Promise<string | undefined>;
  isAuthenticated: () => Promise<boolean>;
}

export interface ResourceChangesConnection {
  start: () => void;
  stop: () => void;
  addListener: (resourceType: ChangedResourceType, resourceId: string, listener: ResourceChangesListener) => () => void;
}

const invokeSubscribe = async (hubConnection: HubConnection, token: string | undefined): Promise<boolean> => {
  if (!token) {
    return false;
  }

  try {
    await hubConnection.invoke("Subscribe", token);
    return true;
  } catch (error) {
    logError(error, "[resourceChangesConnection] Subscribe");
    return false;
  }
};

const getListenerKey = (resourceType: string, resourceId: string) => `${resourceType}:${resourceId.toLowerCase()}`;

export const createResourceChangesConnection = ({
  connect,
  getSubscriptionToken,
  isAuthenticated,
}: Options): ResourceChangesConnection => {
  const listeners = new Map<string, Set<ResourceChangesListener>>();

  let runId = 0;
  let connection: HubConnection | undefined;
  let restartTimer: ReturnType<typeof setTimeout> | undefined;
  let restartAttempt = 0;
  let subscribeId = 0;

  const handleResourceChanged = (notification: ResourceChangedNotification) => {
    const resourceListeners = listeners.get(getListenerKey(notification.resourceType, notification.resourceId));
    [...(resourceListeners ?? [])].forEach((listener) => listener.onChange(notification));
  };

  const notifySubscribed = () => {
    [...listeners.values()]
      .flatMap((resourceListeners) => [...resourceListeners])
      .forEach((listener) => listener.onSubscribed());
  };

  const subscribe = async (hubConnection: HubConnection) => {
    subscribeId += 1;
    const currentSubscribeId = subscribeId;

    const isCurrent = () => currentSubscribeId === subscribeId && hubConnection === connection;

    const token = await getSubscriptionToken();
    if (!isCurrent()) {
      return;
    }

    const isSubscribed = await invokeSubscribe(hubConnection, token);

    if (!isCurrent()) {
      return;
    }

    if (!isSubscribed) {
      // Closing runs the restart, which retries the subscribe with its backoff.
      hubConnection.stop().catch(() => {});
      return;
    }

    restartAttempt = 0;
    notifySubscribed();
  };

  const scheduleRestart = (currentRunId: number) => {
    const delayMs = getRestartDelayMs(restartAttempt);
    restartAttempt += 1;
    restartTimer = setTimeout(() => {
      restartTimer = undefined;
      open(currentRunId);
    }, delayMs);
  };

  const open = async (currentRunId: number) => {
    const hubConnection = await connect();
    if (currentRunId !== runId) {
      await hubConnection?.stop().catch(() => {});
      return;
    }

    if (!hubConnection) {
      if ((await isAuthenticated()) && currentRunId === runId) {
        scheduleRestart(currentRunId);
      }
      return;
    }

    connection = hubConnection;
    hubConnection.on(RESOURCE_CHANGED_EVENT, handleResourceChanged);

    // Group membership belongs to the connection id, which changes on every automatic reconnect.
    hubConnection.onreconnected(() => {
      if (hubConnection === connection) {
        subscribe(hubConnection);
      }
    });

    // Fires when the automatic reconnect gives up or a failed subscribe stops the connection.
    hubConnection.onclose(() => {
      if (hubConnection === connection) {
        connection = undefined;
        scheduleRestart(currentRunId);
      }
    });

    await subscribe(hubConnection);
  };

  return {
    start: () => {
      runId += 1;
      restartAttempt = 0;
      open(runId);
    },
    stop: () => {
      runId += 1;
      clearTimeout(restartTimer);
      restartTimer = undefined;

      const hubConnection = connection;
      connection = undefined;
      hubConnection?.off(RESOURCE_CHANGED_EVENT);
      hubConnection?.stop().catch(() => {});
    },
    addListener: (resourceType, resourceId, listener) => {
      const key = getListenerKey(resourceType, resourceId);
      const resourceListeners = listeners.get(key) ?? new Set<ResourceChangesListener>();
      resourceListeners.add(listener);
      listeners.set(key, resourceListeners);

      return () => {
        resourceListeners.delete(listener);
        if (resourceListeners.size === 0 && listeners.get(key) === resourceListeners) {
          listeners.delete(key);
        }
      };
    },
  };
};
